import {
  DownloadIcon,
  SearchIcon,
  SortAscIcon,
  SortDescIcon,
} from "@primer/octicons-react";
import * as XLSX from "xlsx";

import {
  ActionList,
  ActionMenu,
  Box,
  Button,
  Checkbox,
  Octicon,
  Pagination,
} from "@primer/react";
import clsx from "clsx";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Column, useTable } from "react-table";
import { useDebounce } from "use-debounce";

import { PlaceHolder } from "../place-holder";
import Text from "../text";
import TextInput from "../text-input";
import styles from "./DataTable.module.css";
import { ExportExcel } from "../export-excel";
import { formatVND } from "../../../utils/formatVND";
export enum eSortMode {
  ASC = "asc",
  DESC = "desc",
}
function removeAccents(str: string) {
  if (!str) return str;
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
}
function dynamicSort(property: string, mode: eSortMode) {
  var sortOrder = 1;
  // if (property[0] === "-") {
  //     sortOrder = -1;
  //     property = property.substr(1);
  // }
  if (mode === eSortMode.ASC) {
    sortOrder = 1;
  } else {
    sortOrder = -1;
  }
  return function (a: any, b: any) {
    /* next line works with strings and numbers,
     * and you may want to customize it to your needs
     */
    var result =
      a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
    return result * sortOrder;
  };
}
export interface IFocusCell {
  rowData: any;
  dataField: string;
}
interface ISelectionProps {
  keyExpr?: string;
  mode: "multiple" | "single";
  onSelectionChanged: (keys: number[]) => void;
  selectedRowKeys?: number[];
}
interface ISortConfig {
  enable: boolean;
  field?: string;
  mode: eSortMode;
  onValueChanged?: (field: string, mode: eSortMode) => void;
}
interface IPaging {
  enable: boolean;
  pageSize?: number;
  pageSizeItems?: number[];
  // pageIndex: number,
  // pageCount: number,
  // onPageIndexChanged: (pageIndex: number) => void
}
interface ISummaryItem {
  name: string;
  column: string;
  summaryType: "sum" | "custom";
  render: (value: number) => string | JSX.Element;
  visible?: boolean;
}

interface ISummary {
  calculateCustomSummary?: (column: string, data: any[]) => number;
  items: ISummaryItem[];
}
export interface IColumn {
  caption?: string;
  dataField?: string;
  isMainColumn?: boolean;
  fixed?: boolean;
  fixedPosition?: "left" | "right";
  id?: string;
  summary?: boolean;
  cellRender?: (data: any) => JSX.Element;
  headerRender?: (data: any) => ReactNode;
  columns?: IColumn[];
  width?: number | string | undefined;
  minWidth?: number | string | undefined;
  maxWidth?: number | string | undefined;
  align?: "left" | "center" | "right";
  isAllowFocus?: boolean;
}
interface ICheckDontSaveChange {
  fields: string[];
  rootDataSource: any[];
}
export interface IRangeSelectionKeyDownData {
  keyCode: string;
  rowKeys: any;
  dataFields: string[];
}
interface IRangeSelectionProps {
  onKeyDown: (data: IRangeSelectionKeyDownData) => void;
}
interface IFilterRowConfig {
  enable: boolean;
  onConditionChanged?: (cond: any) => void;
}
interface IDataTableProps {
  width?: string;
  columns: IColumn[];
  data: any[];
  height?: string;
  title?: string;
  titleComponent?: React.ReactNode;
  subTitle?: string;
  subTitleComponent?: React.ReactNode;
  actionComponent?: React.ReactNode;
  isLoading?: boolean;
  paging?: IPaging;
  searchEnable?: boolean;
  sortConfig?: ISortConfig;
  exportEnable?: boolean;
  selection?: ISelectionProps;
  onFocuseCellChanged?: (data: IFocusCell) => void;
  onClick?: (data: IFocusCell) => void;
  focusedCell?: IFocusCell;
  checkDontSaveChange?: ICheckDontSaveChange;
  emptyComponent?: React.ReactNode;
  rangeSelection?: IRangeSelectionProps;
  isNoPaddingCell?: boolean;
  filterRow?: IFilterRowConfig;
  summary?: ISummary;
}
const DataTable = (props: IDataTableProps) => {
  const [search_key, setSearch_key] = useState("");
  const [searchKeyDelayed] = useDebounce(search_key, 1000);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(props.paging?.pageSize ?? 50);

  const [sortConfig, setSortConfig] = useState(props.sortConfig);
  // Trạng thái vùng được chọn
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [isSelecting, setIsSelecting] = useState(false);
  const [startCell, setStartCell] = useState<any>(null);
  const [conditions, setConditions] = useState<any[]>([
    // { field: "", operator: "equals", value: "" },
  ]);
  const [logicOperator, setLogicOperator] = useState("and");

  // State để detect scroll
  const [hasScroll, setHasScroll] = useState(false);
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

  // Calculate fixed column positions (by leaf columns, to support grouped headers)
  const calculateFixedColumnPositions = (columns: IColumn[]) => {
    let leftPosition = 0;
    let rightPosition = 0;

    const parseWidthValue = (w: any) => {
      if (typeof w === "number") return w;
      return parseInt(String(w || "0"), 10) || 0;
    };

    const flattenLeafColumns = (cols: IColumn[]): IColumn[] => {
      const result: IColumn[] = [];
      cols.forEach((c) => {
        if (c.columns && c.columns.length > 0) {
          result.push(...flattenLeafColumns(c.columns as any));
        } else {
          result.push(c);
        }
      });
      return result;
    };

    const leafColumns = flattenLeafColumns(columns);

    // Process left fixed columns
    const leftFixedColumns = leafColumns.filter(
      (col) => col.fixed && (!col.fixedPosition || col.fixedPosition === "left")
    );
    const leftPositions: Record<string, number> = {};

    leftFixedColumns.forEach((col, index) => {
      leftPositions[col.id || col.dataField || index] = leftPosition;
      // Add the width of this column to the running total
      leftPosition += parseWidthValue(col.width ?? col.minWidth);
    });

    // Process right fixed columns
    const rightFixedColumns = leafColumns
      .filter((col) => col.fixed && col.fixedPosition === "right")
      .reverse();
    const rightPositions: Record<string, number> = {};

    rightFixedColumns.forEach((col, index) => {
      rightPositions[col.id || col.dataField || index] = rightPosition;
      // Add the width of this column to the running total
      rightPosition += parseWidthValue(col.width ?? col.minWidth);
    });

    return { leftPositions, rightPositions };
  };

  // Bắt đầu chọn vùng
  const handleMouseDown = (rowIndex: number, colIndex: number) => {
    setIsSelecting(true);
    setStartCell({ rowIndex, colIndex });
    setSelectedCells(new Set([`${rowIndex}-${colIndex}`]));
  };
  // Kéo chuột để chọn vùng
  const handleMouseMove = (rowIndex: number, colIndex: number) => {
    if (isSelecting && startCell) {
      const { rowIndex: startRow, colIndex: startCol } = startCell;

      const minRow = Math.min(startRow, rowIndex);
      const maxRow = Math.max(startRow, rowIndex);
      const minCol = Math.min(startCol, colIndex);
      const maxCol = Math.max(startCol, colIndex);

      const newSelectedCells = new Set();
      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          newSelectedCells.add(`${r}-${c}`);
        }
      }
      setSelectedCells(newSelectedCells);
    }
  };

  // Kết thúc chọn vùng
  const handleMouseUp = () => {
    setIsSelecting(false);
    setStartCell(null);
  };
  const {
    data,
    title,
    titleComponent,
    subTitle,
    subTitleComponent,
    actionComponent,
    isLoading,
    paging,
    height,
    searchEnable,

    exportEnable,
  } = props;
  const onFilterChange = (field: any, operator?: any, value?: any) => {
    if (!value) {
      removeCondition(field);
      return;
    }
    const cond = conditions.find((x) => x.field === field);

    if (cond) {
      updateCondition(field, operator, value);
    } else {
      addCondition(field, operator, value);
    }
  };
  // Thêm điều kiện mới
  const addCondition = (field: any, operator?: any, value?: any) => {
    setConditions([
      ...conditions.filter((x) => x.field && x.field !== field),
      { field: field, operator: operator ?? "equals", value: value ?? "" },
    ]);
  };
  // Xóa điều kiện
  const removeCondition = (field: any) => {
    const newConditions = conditions.filter((x) => x.field !== field);
    setConditions(newConditions);
  };
  // Cập nhật điều kiện
  const updateCondition = (field: any, operator?: any, value?: any) => {
    setConditions(
      conditions.map((c) => {
        if (c.field === field) {
          return {
            ...c,
            operator: operator ?? "equals",
            value,
          };
        }
        return c;
      })
    );
  };
  const sort_by = useMemo(() => {
    return sortConfig?.field ?? "";
  }, [sortConfig]);
  useEffect(() => {
    if (props.paging) {
      setPageSize(props.paging.pageSize ?? 50);
    }
  }, [props.paging]);

  // Detect scroll để điều chỉnh pagination position
  useEffect(() => {
    const checkScroll = () => {
      if (containerElement) {
        const hasHorizontalScroll = containerElement.scrollWidth > containerElement.clientWidth;
        setHasScroll(hasHorizontalScroll);
      }
    };

    if (containerElement) {
      // Check scroll khi component mount và khi data thay đổi
      checkScroll();

      // Add resize observer để check khi size thay đổi
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(containerElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [containerElement, data, props.columns]);
  useEffect(() => {
    setPageIndex(0);
  }, [sort_by]);
  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (selectedCells.size > 0 && rows.length > 0) {
        let rowIndexs: number[] = [];
        let colIndexs: number[] = [];
        selectedCells.forEach((cell: any) => {
          const [rowIndx, colIndex] = cell
            .split("-")
            .map((c: any) => parseInt(c));
          if (!rowIndexs.includes(rowIndx)) rowIndexs.push(rowIndx);
          if (!colIndexs.includes(colIndex)) colIndexs.push(colIndex);
          // const rowData = pagedData[rowIndx];
          // const colData = columns[colIndex];
        });
        rowIndexs = Array.from(new Set(rowIndexs));
        colIndexs = Array.from(new Set(colIndexs));
        let rowKeys: number[] = [];
        let dataFields: string[] = [];
        rowIndexs.forEach((rowIndex) => {
          const rowData = rows[rowIndex].original;
          if (rowData) rowKeys.push(rowData.id);
        });
        colIndexs.forEach((colIndex) => {
          const cell = rows[0].cells[colIndex];
          if (cell) {
            const column: any = cell.column;
            dataFields.push(column?.dataField ?? "");
          }
        });

        if (props.rangeSelection && props.rangeSelection.onKeyDown) {
          props.rangeSelection.onKeyDown({
            dataFields,
            rowKeys,
            keyCode: e.key,
          });
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Dọn dẹp sự kiện khi component unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedCells]);
  const sort_by_name = useMemo(() => {
    return props.columns
      .filter((x) => x.caption === sort_by)
      .map((x) => x.caption)
      .join(",");
  }, [props.columns, sort_by]);

  // Memoize column categories for search optimization
  const columnCategories = useMemo(() => {
    const fastColumns = props.columns.filter((col) => col.dataField && !col.cellRender);
    const slowColumns = props.columns.filter((col) => col.cellRender);
    const otherColumns = props.columns.filter((col) => !col.dataField && !col.cellRender && col.id);
    
    // Pre-compute checkedFields Set once
    const checkedFields = new Set<string>();
    fastColumns.forEach((col) => {
      if (col.dataField) checkedFields.add(col.dataField);
    });
    otherColumns.forEach((col) => {
      if (col.id) checkedFields.add(col.id);
    });
    slowColumns.forEach((col) => {
      if (col.dataField) checkedFields.add(col.dataField);
      if (col.id) checkedFields.add(col.id);
    });
    
    return { fastColumns, slowColumns, otherColumns, checkedFields };
  }, [props.columns]);

  const filterdData: any[] = useMemo(() => {
    let temp = data;
    
    // Apply searchEnable filter if enabled and has search key
    if (searchEnable && searchKeyDelayed && searchKeyDelayed.trim() !== "") {
      const searchKeyNormalized = removeAccents(searchKeyDelayed.toLowerCase());
      const searchKeyLength = searchKeyNormalized.length;
      const { fastColumns, slowColumns, otherColumns, checkedFields } = columnCategories;
      
      temp = data.filter((x) => {
        // Fast path: search in dataField first (no rendering needed)
        for (let i = 0; i < fastColumns.length; i++) {
          const col = fastColumns[i];
          const value = x[col.dataField!];
          if (value != null) {
            const valueStr = String(value);
            if (valueStr.length >= searchKeyLength) {
              const valueNormalized = removeAccents(valueStr.toLowerCase());
              if (valueNormalized.includes(searchKeyNormalized)) {
                return true;
              }
            }
          }
        }
        
        // Medium path: search in other columns with id
        for (let i = 0; i < otherColumns.length; i++) {
          const col = otherColumns[i];
          const value = x[col.id!];
          if (value != null) {
            const valueStr = String(value);
            if (valueStr.length >= searchKeyLength) {
              const valueNormalized = removeAccents(valueStr.toLowerCase());
              if (valueNormalized.includes(searchKeyNormalized)) {
                return true;
              }
            }
          }
        }
        
        // Slow path: search in columns with cellRender (only if needed)
        for (let i = 0; i < slowColumns.length; i++) {
          const col = slowColumns[i];
          let cellValue = "";
          
          // Try dataField first (faster than cellRender)
          if (col.dataField && x[col.dataField]) {
            cellValue = String(x[col.dataField]);
          } else {
            // Only call cellRender if dataField doesn't exist
            try {
              const rendered = col.cellRender!(x);
              if (typeof rendered === "string") {
                cellValue = rendered;
              } else if (React.isValidElement(rendered)) {
                const element = rendered as React.ReactElement;
                if (element.props?.children) {
                  if (typeof element.props.children === "string") {
                    cellValue = element.props.children;
                  } else if (Array.isArray(element.props.children)) {
                    cellValue = element.props.children
                      .map((child: any) => (typeof child === "string" ? child : ""))
                      .join(" ");
                  } else if (element.props.children?.props?.text) {
                    cellValue = element.props.children.props.text;
                  }
                }
              }
            } catch (e) {
              // Ignore errors in cellRender
            }
          }
          
          if (cellValue && cellValue.length >= searchKeyLength) {
            const cellValueNormalized = removeAccents(cellValue.toLowerCase());
            if (cellValueNormalized.includes(searchKeyNormalized)) {
              return true;
            }
          }
        }
        
        // Last resort: search in all remaining fields (skip already checked fields)
        for (const key in x) {
          if (x.hasOwnProperty(key) && !checkedFields.has(key) && x[key] != null) {
            const fieldValue = String(x[key]);
            if (fieldValue.length >= searchKeyLength) {
              const fieldValueNormalized = removeAccents(fieldValue.toLowerCase());
              if (fieldValueNormalized.includes(searchKeyNormalized)) {
                return true;
              }
            }
          }
        }
        
        return false;
      });
    }

    // Only sort if sort_by is specified
    let tempSort = temp;
    if (sort_by) {
      // Create a copy before sorting to avoid mutating the original array
      tempSort = [...temp].sort(
        dynamicSort(sort_by, sortConfig?.mode ?? eSortMode.DESC)
      );
    }
    let resultData: any[] = tempSort;
    
    // Early return if no filter conditions
    if (!conditions || conditions.length === 0) {
      return tempSort;
    }
    
    // Build filter expression and cache normalized values for "contains" operator
    const filterConditions = conditions.map((cond: any) => {
      const normalizedValue = cond.operator === "contains" && cond.value
        ? removeAccents(String(cond.value).toLowerCase())
        : null;
      return {
        field: cond.field,
        operator: cond.operator,
        value: cond.value,
        normalizedValue: normalizedValue,
      };
    });

    if (!filterConditions || filterConditions.length === 0) {
      return tempSort;
    }
    
    // Optimize filter: separate "and" and "or" logic for better performance
    resultData = tempSort.filter((item) => {
      // If using "and" operator, all conditions must pass
      if (logicOperator === "and") {
        for (let i = 0; i < filterConditions.length; i++) {
          const { field, operator, value, normalizedValue } = filterConditions[i];
          const itemValue = item[field];

          let conditionResult;
          switch (operator) {
            case "equals":
              conditionResult = itemValue === value;
              break;
            case "contains":
              if (normalizedValue) {
                const itemValueStr = String(itemValue || "").toLowerCase();
                conditionResult = removeAccents(itemValueStr).includes(normalizedValue);
              } else {
                conditionResult = true;
              }
              break;
            case "greaterThan":
              conditionResult = Number(itemValue) > Number(value);
              break;
            case "lessThan":
              conditionResult = Number(itemValue) < Number(value);
              break;
            case "greaterThanOrEqual":
              conditionResult = Number(itemValue) >= Number(value);
              break;
            case "lessThanOrEqual":
              conditionResult = Number(itemValue) <= Number(value);
              break;
            default:
              conditionResult = true;
          }
          
          // Early return for "and" if any condition fails
          if (!conditionResult) {
            return false;
          }
        }
        return true;
      } else {
        // If using "or" operator, at least one condition must pass
        for (let i = 0; i < filterConditions.length; i++) {
          const { field, operator, value, normalizedValue } = filterConditions[i];
          const itemValue = item[field];

          let conditionResult;
          switch (operator) {
            case "equals":
              conditionResult = itemValue === value;
              break;
            case "contains":
              if (normalizedValue) {
                const itemValueStr = String(itemValue || "").toLowerCase();
                conditionResult = removeAccents(itemValueStr).includes(normalizedValue);
              } else {
                conditionResult = true;
              }
              break;
            case "greaterThan":
              conditionResult = Number(itemValue) > Number(value);
              break;
            case "lessThan":
              conditionResult = Number(itemValue) < Number(value);
              break;
            case "greaterThanOrEqual":
              conditionResult = Number(itemValue) >= Number(value);
              break;
            case "lessThanOrEqual":
              conditionResult = Number(itemValue) <= Number(value);
              break;
            default:
              conditionResult = true;
          }
          
          // Early return for "or" if any condition passes
          if (conditionResult) {
            return true;
          }
        }
        return false;
      }
    });
    return resultData;
  }, [
    searchKeyDelayed,
    searchEnable,
    data,
    columnCategories,
    sort_by,
    sortConfig?.mode,
    conditions,
    logicOperator,
  ]);
  const pageCount = useMemo(() => {
    if (pageSize <= 0) return filterdData.length;
    const c = Math.floor(filterdData.length / pageSize);
    if (c * pageSize < filterdData.length) return c + 1;
    return c;
  }, [filterdData.length, pageSize]);

  const pagedData = useMemo(() => {
    if (paging?.enable === true) {
      const data = filterdData.slice(
        pageSize * pageIndex,
        pageSize * pageIndex + pageSize
      );
      return data;
    }
    return filterdData;
  }, [filterdData, pageIndex, pageSize, paging?.enable]);
  const settingColum = (column: IColumn): Column => {
    const parseWidth = (w: any): number => {
      if (typeof w === "number") return w;
      if (typeof w === "string") {
        const match = w.match(/(\d+(\.\d+)?)/);
        return match ? Number(match[1]) : 0;
      }
      return 0;
    };

    const mappedChildren =
      column.columns && column.columns.length > 0
        ? column.columns.map((x) => settingColum(x))
        : undefined;

    let width = column.width;
    let minWidth = column.minWidth;

    if (!width && mappedChildren && mappedChildren.length > 0) {
      const childWidthSum = mappedChildren.reduce(
        (sum, c: any) => sum + parseWidth(c.width || c.minWidth),
        0
      );
      width = childWidthSum > 0 ? childWidthSum : width;
    }

    if (!minWidth && mappedChildren && mappedChildren.length > 0) {
      const childMinSum = mappedChildren.reduce(
        (sum, c: any) => sum + parseWidth(c.minWidth || c.width),
        0
      );
      minWidth = childMinSum > 0 ? childMinSum : minWidth;
    }

    // Kế thừa trạng thái fixed từ cột con (ưu tiên left, nếu không có thì lấy right)
    let fixed = column.fixed;
    let fixedPosition = column.fixedPosition;
    if (mappedChildren && mappedChildren.length > 0) {
      const hasLeft = mappedChildren.some(
        (c: any) => c.fixed && (c.fixedPosition === "left" || !c.fixedPosition)
      );
      const hasRight = mappedChildren.some(
        (c: any) => c.fixed && c.fixedPosition === "right"
      );
      if (hasLeft) {
        fixed = true;
        fixedPosition = "left";
      } else if (hasRight) {
        fixed = true;
        fixedPosition = "right";
      }
    }

    const col: any = {
      ...column,
      width,
      minWidth,
      fixed,
      fixedPosition,
      columns: mappedChildren,
      Header: column.caption ?? "",
      accessor: column.dataField ?? column.id ?? "",
    };
    return col;
  };
  // height used to stack sticky header rows (group headers + leaf headers + filter row)
  const headerRowHeight = 30;
  const getCellValue = (col: any, row: any): string | number | boolean => {
    if (col.cellRender) {
      const rendered = col.cellRender(row);
      if (
        typeof rendered === "string" ||
        typeof rendered === "number" ||
        typeof rendered === "boolean"
      ) {
        return rendered;
      }
      if (React.isValidElement(rendered)) {
        const element = rendered as React.ReactElement;

        if (element.props.children) {
          if (typeof element.props.children === "string") {
            return element.props.children;
          }
          if (Array.isArray(element.props.children)) {
            return element.props.children
              .map((child: any) => (typeof child === "string" ? child : ""))
              .join(" ");
          }
        }
      }
    }

    return row[col.id] ?? "";
  };

  const columns: any[] = useMemo(() => {
    let col = props.columns.map((x) => {
      return settingColum(x);
    });
    if (props.selection?.mode === "multiple") {
      const selectionCol: any = {
        id: "selection",
        caption: "",
        width: 40,
        fixed: true,
        fixedPosition: "left",
        align: "center",
        cellRender: (data: any) => {
          const id = data.id;
          return (
            <Checkbox
              checked={(props.selection?.selectedRowKeys ?? []).includes(id)}
              disabled={data.disabled}
              onChange={(e) => {
                if (e.target.checked) {
                  props.selection?.onSelectionChanged([
                    ...(props.selection?.selectedRowKeys ?? []),
                    id,
                  ]);
                } else {
                  props.selection?.onSelectionChanged([
                    ...(props.selection?.selectedRowKeys ?? []).filter(
                      (x) => x !== id
                    ),
                  ]);
                }
              }}
            />
          );
        },
        headerRender: () => {
          return (
            <Checkbox
              checked={
                filterdData.filter((g) => !g.disabled).length > 0 &&
                filterdData
                  .filter((g) => !g.disabled)
                  .find(
                    (x) =>
                      !(props.selection?.selectedRowKeys ?? []).includes(x.id)
                  ) === undefined
              }
              onChange={(e) => {
                const ids = filterdData
                  .filter((g) => !g.disabled)
                  ?.map((x) => x.id);
                if (e.target.checked) {
                  props.selection?.onSelectionChanged(
                    Array.from(
                      new Set([
                        ...(props.selection?.selectedRowKeys ?? []),
                        ...ids,
                      ])
                    )
                  );
                } else {
                  props.selection?.onSelectionChanged(
                    Array.from(
                      new Set(
                        (props.selection?.selectedRowKeys ?? []).filter(
                          (x) => !ids.includes(x)
                        )
                      )
                    )
                  );
                }
              }}
            />
          );
        },
      };
      return [selectionCol, ...col];
    }
    return col;
  }, [props.columns, props.selection?.mode, props.selection?.selectedRowKeys]);

  // Calculate fixed column positions once
  const { leftPositions, rightPositions } = useMemo(() => {
    return calculateFixedColumnPositions(columns);
  }, [columns]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
  } = useTable<any>({ columns, data: pagedData });

  //summary column

  const summaryData = useMemo(() => {
    if (!props.data) return {};

    const totals: { [key: string]: number } = {};

    // Process columns with summary attribute
    const columnsWithSummary = props.columns.filter(
      (col) => col.summary === true
    );
    columnsWithSummary.forEach((column) => {
      if (column.dataField) {
        const sum = props.data.reduce(
          (sum, row) => sum + (Number(row[column.dataField!]) || 0),
          0
        );
        totals[column.dataField] = sum;
      }
    });

    // Process summary items from props.summary
    if (props.summary && props.summary.items) {
      props.summary.items.forEach((item) => {
        if (item.summaryType === "sum") {
          const sum = props.data.reduce(
            (sum, row) => sum + (Number(row[item.column]) || 0),
            0
          );
          totals[item.column] = sum;
        } else if (
          item.summaryType === "custom" &&
          props.summary?.calculateCustomSummary
        ) {
          totals[item.column] = props.summary.calculateCustomSummary(
            item.column,
            props.data
          );
        }
      });
    }

    return totals;
  }, [props.summary, props.data, props.columns]);

  // Check if summary row should be visible
  const shouldShowSummaryRow = useMemo(() => {
    // Only show if props.summary is provided
    if (!props.summary) {
      return false;
    }
    
    // If no summary items, don't show
    if (!props.summary.items || props.summary.items.length === 0) {
      return false;
    }
    
    // Check if at least one summary item is visible (default to true if not specified)
    const hasVisibleItem = props.summary.items.some(
      (item) => item.visible !== false
    );
    
    return hasVisibleItem;
  }, [props.summary]);

  return (
    <Box>
      <Box id="toolbar" sx={{ display: "flex", mb: 1 }}>
        <Box
          id="left"
          sx={{
            flex: 1,
          }}
        >
          {props.title && (
            // <Box className={styles.title}>
            //     {title}
            // </Box>
            <Text
              text={props.title}
              sx={{
                fontWeight: 600,
                fontSize: 24,
              }}
            />
          )}
          {props.titleComponent && <Box>{titleComponent}</Box>}
          {props.subTitle && <Box className={styles.subTitle}>{subTitle}</Box>}
          {props.subTitleComponent && <Box>{subTitleComponent}</Box>}
        </Box>
        <Box
          id="actions"
          sx={{
            display: "flex",
            alignItems: "flex-start",
          }}
        >
          {actionComponent}
          {exportEnable && (
            <Box sx={{ ml: 2 }}>
              <ExportExcel
                data={props.data}
                columns={columns}
                filename="DataTableExport.xlsx"
                variant="default"
                size="small"
                buttonText="Export"
              />
            </Box>
          )}
          {searchEnable && (
            <Box sx={{ ml: 2 }}>
              <TextInput
                leadingVisual={SearchIcon}
                placeholder={"Search"}
                value={search_key}
                onChange={(e) => {
                  setSearch_key(e.target.value);
                }}
              ></TextInput>
            </Box>
          )}
          {sortConfig && sortConfig.enable && (
            <Box sx={{ ml: 2 }}>
              <ActionMenu>
                <ActionMenu.Button
                  leadingVisual={
                    sortConfig?.mode === eSortMode.DESC
                      ? SortDescIcon
                      : SortAscIcon
                  }
                >
                  Sort by <b>{sort_by_name}</b>
                </ActionMenu.Button>
                <ActionMenu.Overlay>
                  <ActionList
                    selectionVariant="single"
                    role="menu"
                    aria-label=""
                  >
                    {props.columns
                      .filter((x) => x.dataField)
                      .map((x) => {
                        return (
                          <ActionList.Item
                            key={x.id}
                            role="menuitemcheckbox"
                            selected={x.dataField === sortConfig.field}
                            onSelect={() => {
                              setSortConfig({
                                ...sortConfig,
                                field: x.dataField ?? "",
                              });
                            }}
                          >
                            {x.caption}
                          </ActionList.Item>
                        );
                      })}

                    <ActionList.Divider></ActionList.Divider>
                    <ActionList.Item
                      role="menuitemcheckbox"
                      selected={sortConfig.mode === eSortMode.ASC}
                      onSelect={() => {
                        setSortConfig({
                          ...sortConfig,
                          mode: eSortMode.ASC,
                        });
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <SortAscIcon />
                      </ActionList.LeadingVisual>
                      Ascending
                    </ActionList.Item>
                    <ActionList.Item
                      role="menuitemcheckbox"
                      selected={sortConfig.mode === eSortMode.DESC}
                      onSelect={() => {
                        setSortConfig({
                          ...sortConfig,
                          mode: eSortMode.DESC,
                        });
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <SortDescIcon />
                      </ActionList.LeadingVisual>
                      Descending
                    </ActionList.Item>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </Box>
          )}
        </Box>
      </Box>
      <Box
        id="wrapper"
        sx={{
          position: "relative",
        }}
      >
        <Box
          className={clsx(styles.container, hasScroll && styles.hasScroll)}
          sx={{
            overflow: "auto",
            height: props.height ?? "auto",
            position: "relative",
            width: "100%",
            display: "block" /* Ensures the container takes full width */,
            paddingBottom: paging?.enable ? "50px" : "0px", // Space for pagination when enabled
          }}
          ref={(el) => {
            setContainerElement(el);
            if (el) {
              // Add scroll event listener to show/hide scroll indicators
              el.addEventListener("scroll", () => {
                const hasHorizontalScroll = el.scrollWidth > el.clientWidth;
                const isScrolledRight =
                  hasHorizontalScroll && el.scrollLeft > 0;
                const isScrolledToEnd =
                  hasHorizontalScroll &&
                  Math.abs(el.scrollWidth - el.clientWidth - el.scrollLeft) < 2;

                if (isScrolledRight && !isScrolledToEnd) {
                  el.classList.add("scrolled-left", "scrolled-right");
                } else if (isScrolledRight) {
                  el.classList.add("scrolled-left");
                  el.classList.remove("scrolled-right");
                } else if (hasHorizontalScroll) {
                  el.classList.add("scrolled-right");
                  el.classList.remove("scrolled-left");
                } else {
                  el.classList.remove("scrolled-left", "scrolled-right");
                }
              });

              // Initial check
              const hasHorizontalScroll = el.scrollWidth > el.clientWidth;
              if (hasHorizontalScroll) {
                el.classList.add("scrolled-right");
              }
            }
          }}
        >
          <table
            {...getTableProps()}
            className={clsx(
              styles.myTable,
              props.isNoPaddingCell ? styles.noPadding : undefined,
              props.rangeSelection ? styles.rangeSelection : undefined,
              paging?.enable === true ? styles.hasPaging : ""
            )}
            onMouseUp={props.rangeSelection ? handleMouseUp : undefined}
          >
            <thead 
              className={styles.header}
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "#F5F8FA",
              }}
            >
              {headerGroups.map((headerGroup: any, hIdx) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={hIdx}>
                  {headerGroup.headers.map((column: any, idx: number) => {
                    const fixedTarget = column.placeholderOf ?? column;
                    const width = column.placeholderOf
                      ? column.placeholderOf.width
                      : column.width;
                    const minWidth = column.placeholderOf
                      ? column.placeholderOf.minWidth
                      : column.minWidth;
                    const isFixed = fixedTarget.fixed;
                    const fixedPosition = fixedTarget.fixedPosition;
                    // Sticky all header rows (including group header row like "Tổng")
                    const topOffset = `${hIdx * headerRowHeight}px`;
                    const fixedKey = fixedTarget.id || fixedTarget.dataField || idx;
                    return (
                      <th
                        {...column.getHeaderProps()}
                        style={{
                          textAlign: column.align,
                          width,
                          minWidth,
                          position: "sticky",
                          zIndex: isFixed ? 10 : 3,
                          top: topOffset,
                          ...(isFixed && (fixedPosition === "left" || !fixedPosition)
                            ? {
                                left: `${
                                  leftPositions[
                                    fixedKey
                                  ]
                                }px`,
                                borderBottom: "1px solid #d0d7de",
                                backgroundColor: "#F5F8FA",
                                // boxShadow: '1px 0 0 0 #e1e4e8',
                                transition: "background-color 0.15s ease",
                              }
                            : {}),
                          ...(isFixed && fixedPosition === "right"
                            ? {
                                right: `${
                                  rightPositions[
                                    fixedKey
                                  ]
                                }px`,
                                borderBottom: "1px solid #d0d7de",
                                backgroundColor: "#F5F8FA",
                                // boxShadow: '-1px 0 0 0 #e1e4e8',
                                transition: "background-color 0.15s ease",
                              }
                            : {}),
                          ...(!isFixed
                            ? {
                                backgroundColor: "#F5F8FA",
                                borderBottom: "1px solid #d0d7de",
                              }
                            : {}),
                        }}
                        className={clsx(
                          isFixed && styles.fixed_column_header,
                          isFixed && fixedPosition === "right"
                            ? styles.fixed_column_header_right
                            : isFixed
                            ? styles.fixed_column_header_left
                            : undefined
                        )}
                        key={idx}
                      >
                        {column.headerRender
                          ? column.headerRender()
                          : column.render("Header")}
                      </th>
                    );
                  })}
                </tr>
              ))}
              {props.filterRow?.enable !== false && (
                <tr 
                  role="row"
                  style={{
                    position: "sticky",
                    top: `${headerGroups.length * headerRowHeight}px`,
                    zIndex: 11,
                    backgroundColor: "#F5F8FA",
                  }}
                >
                  {allColumns.map((column: any, idx: number) => {
                    const fixedTarget = column.placeholderOf ?? column;
                    const width =
                      column.placeholderOf?.width ??
                      column.placeholderOf?.minWidth ??
                      column.width ??
                      column.minWidth;
                    const isFixed = fixedTarget.fixed;
                    const fixedPosition = fixedTarget.fixedPosition;
                    const topOffset = `${headerGroups.length * headerRowHeight}px`;
                    const fixedKey = fixedTarget.id || fixedTarget.dataField || idx;
                    return (
                      <th
                        key={`filter_${idx}`}
                        style={{
                          width,
                          position: "sticky",
                          zIndex: isFixed ? 11 : 4,
                          backgroundColor:
                            "#F5F8FA" /* Background for all columns to prevent see-through */,
                          top: topOffset /* Top position for fixed columns */,
                          ...(isFixed && (fixedPosition === "left" || !fixedPosition)
                            ? {
                                left: `${
                                  leftPositions[
                                    fixedKey
                                  ]
                                }px`,
                                borderBottom: "1px solid #d0d7de",
                                transition: "background-color 0.15s ease",
                              }
                            : {}),
                          ...(isFixed && fixedPosition === "right"
                            ? {
                                right: `${
                                  rightPositions[
                                    fixedKey
                                  ]
                                }px`,
                                borderBottom: "1px solid #d0d7de",
                                transition: "background-color 0.15s ease",
                              }
                            : {}),
                        }}
                        className={clsx(
                          isFixed &&
                            styles.fixed_column_header /* Use header class for better styling */,
                          isFixed && fixedPosition === "right"
                            ? styles.fixed_column_header_right
                            : isFixed
                            ? styles.fixed_column_header_left
                            : undefined
                        )}
                      >
                        {column.dataField && (
                          <Box sx={{ margin: "-0.5rem -0.75rem" }}>
                            <TextInput
                              block
                              leadingVisual={
                                <Box sx={{ color: "#656d76" }}>
                                  <Octicon
                                    icon={SearchIcon}
                                    sx={{ width: "12px", height: "12px" }}
                                  />
                                </Box>
                              }
                              sx={{
                                backgroundColor: "#F5F8FA",
                                boxShadow: "none",
                                border: "0!important",
                                mt: "1px",
                              }}
                              onValueDelayChanged={(value) => {
                                onFilterChange(
                                  column.dataField,
                                  "contains",
                                  value
                                );
                              }}
                            />
                          </Box>
                        )}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>
            {isLoading && (
              <tbody {...getTableBodyProps()}>
                {headerGroups.map((headerGroup: any, groupIdx: number) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={`loading_row_${groupIdx}`}
                  >
                    {headerGroup.headers.map((column: any, idx: number) => {
                      return (
                        <td
                          {...column.getHeaderProps()}
                          key={`loading_cell_${groupIdx}_${idx}`}
                          style={{
                            width: column.placeholderOf
                              ? column.placeholderOf.width ??
                                column.placeholderOf.minWidth
                              : column.width ?? column.minWidth,
                            ...(column.fixed && column.fixedPosition === "left"
                              ? {
                                  left: `${
                                    leftPositions[
                                      column.id || column.dataField || idx
                                    ]
                                  }px`,
                                  borderBottom: "1px solid #d0d7de",
                                  // boxShadow: '1px 0 0 0 #e1e4e8',
                                  transition: "background-color 0.15s ease",
                                }
                              : {}),
                            ...(column.fixed && column.fixedPosition === "right"
                              ? {
                                  right: `${
                                    rightPositions[
                                      column.id || column.dataField || idx
                                    ]
                                  }px`,
                                  borderBottom: "1px solid #d0d7de",
                                  // boxShadow: '-1px 0 0 0 #e1e4e8',
                                  transition: "background-color 0.15s ease",
                                }
                              : {}),
                          }}
                          className={clsx(
                            column.fixed && styles.fixed_column,
                            column.fixed && column.fixedPosition === "right"
                              ? styles.fixed_column_right
                              : column.fixed
                              ? styles.fixed_column_left
                              : undefined
                          )}
                        >
                          <Box sx={{ m: "-1rem", mb: "-1.8rem" }}>
                            <PlaceHolder line_number={1} />
                          </Box>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {headerGroups.map((headerGroup: any, groupIdx: number) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={`loading_row_2_${groupIdx}`}
                  >
                    {headerGroup.headers.map((column: any, idx: number) => {
                      return (
                        <td
                          {...column.getHeaderProps()}
                          key={`loading_cell_2_${groupIdx}_${idx}`}
                          style={{
                            width: column.placeholderOf
                              ? column.placeholderOf.width ??
                                column.placeholderOf.minWidth
                              : column.width ?? column.minWidth,
                            ...(column.fixed && column.fixedPosition === "left"
                              ? {
                                  left: `${
                                    leftPositions[
                                      column.id || column.dataField || idx
                                    ]
                                  }px`,
                                  borderBottom: "1px solid #d0d7de",
                                  // boxShadow: '1px 0 0 0 #e1e4e8',
                                  transition: "background-color 0.15s ease",
                                }
                              : {}),
                            ...(column.fixed && column.fixedPosition === "right"
                              ? {
                                  right: `${
                                    rightPositions[
                                      column.id || column.dataField || idx
                                    ]
                                  }px`,
                                  borderBottom: "1px solid #d0d7de",
                                  // boxShadow: '-1px 0 0 0 #e1e4e8',
                                  transition: "background-color 0.15s ease",
                                }
                              : {}),
                          }}
                          className={clsx(
                            column.fixed && styles.fixed_column,
                            column.fixed && column.fixedPosition === "right"
                              ? styles.fixed_column_right
                              : column.fixed
                              ? styles.fixed_column_left
                              : undefined
                          )}
                        >
                          <Box sx={{ m: "-1rem", mb: "-1.8rem" }}>
                            <PlaceHolder line_number={1} />
                          </Box>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {headerGroups.map((headerGroup: any, groupIdx: number) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={`loading_row_3_${groupIdx}`}
                  >
                    {headerGroup.headers.map((column: any, idx: number) => {
                      return (
                        <td
                          {...column.getHeaderProps()}
                          key={`loading_cell_3_${groupIdx}_${idx}`}
                          style={{
                            width: column.placeholderOf
                              ? column.placeholderOf.width ??
                                column.placeholderOf.minWidth
                              : column.width ?? column.minWidth,
                            ...(column.fixed && column.fixedPosition === "left"
                              ? {
                                  left: `${
                                    leftPositions[
                                      column.id || column.dataField || idx
                                    ]
                                  }px`,
                                  borderBottom: "1px solid #d0d7de",
                                  // boxShadow: '1px 0 0 0 #e1e4e8',
                                  transition: "background-color 0.15s ease",
                                }
                              : {}),
                            ...(column.fixed && column.fixedPosition === "right"
                              ? {
                                  right: `${
                                    rightPositions[
                                      column.id || column.dataField || idx
                                    ]
                                  }px`,
                                  borderBottom: "1px solid #d0d7de",
                                  // boxShadow: '-1px 0 0 0 #e1e4e8',
                                  transition: "background-color 0.15s ease",
                                }
                              : {}),
                          }}
                          className={clsx(
                            column.fixed && styles.fixed_column,
                            column.fixed && column.fixedPosition === "right"
                              ? styles.fixed_column_right
                              : column.fixed
                              ? styles.fixed_column_left
                              : undefined
                          )}
                        >
                          <Box sx={{ m: "-1rem", mb: "-1.8rem" }}>
                            <PlaceHolder line_number={1} />
                          </Box>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            )}
            {!isLoading && (
              <>
                {pagedData.length > 0 && (
                  <tbody {...getTableBodyProps()}>
                    {rows.map((row: any, rowIndex: number) => {
                      prepareRow(row);
                      let isSelectedSigle: boolean = false;
                      if (props.selection) {
                        if (props.selection.mode === "single") {
                          if (
                            props.selection.selectedRowKeys &&
                            props.selection.selectedRowKeys.length > 0 &&
                            props.selection.selectedRowKeys[0] ===
                              row.original.id
                          ) {
                            isSelectedSigle = true;
                          }
                        }
                      }
                      return (
                        <tr
                          {...row.getRowProps()}
                          onClick={() => {
                            if (
                              props.selection &&
                              props.selection.mode === "single"
                            ) {
                              props.selection.onSelectionChanged([
                                row.original.id,
                              ]);
                            }
                          }}
                        >
                          {row.cells.map((cell: any, colIndex: number) => {
                            let isDontSave: boolean = false;
                            if (
                              props.checkDontSaveChange &&
                              cell.column.isAllowFocus
                            ) {
                              const rootRowData =
                                props.checkDontSaveChange.rootDataSource.find(
                                  (x) => x.id === row.original.id
                                );
                              if (rootRowData) {
                                const rootValue =
                                  rootRowData[cell.column.dataField];
                                const currentValue =
                                  row.original[cell.column.dataField];
                                if (rootValue !== currentValue) {
                                  isDontSave = true;
                                }
                              }
                            }
                            return (
                              <td
                                {...cell.getCellProps()}
                                style={{
                                  width: cell.column.width,
                                  minWidth: cell.column.minWidth,
                                  fontWeight: cell.column.isMainColumn
                                    ? "600"
                                    : 400,
                                  whiteSpace: "break-spaces",
                                  textAlign: cell.column.align,
                                  backgroundColor: isSelectedSigle
                                    ? "#fff8c5"
                                    : undefined,
                                  borderBottom: "1px solid #d0d7de",
                                  position: "relative",
                                  zIndex: cell.column.fixed ? 1 : 0,

                                  ...(cell.column.fixed &&
                                  cell.column.fixedPosition === "left"
                                    ? {
                                        left: `${
                                          leftPositions[
                                            cell.column.id ||
                                              cell.column.dataField ||
                                              colIndex
                                          ]
                                        }px`,
                                        transition:
                                          "background-color 0.15s ease",
                                        position: "sticky",
                                        backgroundColor: "#fff",
                                        zIndex: 1,
                                      }
                                    : {}),

                                  ...(cell.column.fixed &&
                                  cell.column.fixedPosition === "right"
                                    ? {
                                        right: `${
                                          rightPositions[
                                            cell.column.id ||
                                              cell.column.dataField ||
                                              colIndex
                                          ]
                                        }px`,
                                        transition:
                                          "background-color 0.15s ease",
                                        position: "sticky",
                                        backgroundColor: "#fff",
                                        zIndex: 1,
                                      }
                                    : {}),
                                }}
                                tabIndex={
                                  cell.column.isAllowFocus ? 0 : undefined
                                }
                                onFocus={() => {
                                  if (props.onFocuseCellChanged) {
                                    props.onFocuseCellChanged({
                                      rowData: row.original,
                                      dataField: cell.column.dataField,
                                    });
                                  }
                                }}
                                onClick={() => {
                                  if (props.onClick) {
                                    props.onClick({
                                      rowData: row.original,
                                      dataField: cell.column.dataField,
                                    });
                                  }
                                }}
                                className={clsx(
                                  cell.column.fixed && styles.fixed_column,
                                  cell.column.fixed &&
                                    cell.column.fixedPosition === "right"
                                    ? styles.fixed_column_right
                                    : cell.column.fixed
                                    ? styles.fixed_column_left
                                    : undefined,
                                  props.focusedCell &&
                                    props.focusedCell.dataField ===
                                      cell.column.dataField &&
                                    props.focusedCell.rowData.id ===
                                      row.original.id
                                    ? styles.focused
                                    : "",
                                  isDontSave ? styles.isDoneSave : "",
                                  selectedCells.has(`${rowIndex}-${colIndex}`)
                                    ? styles.rangeSelected
                                    : ""
                                )}
                                onMouseDown={
                                  props.rangeSelection
                                    ? () => handleMouseDown(rowIndex, colIndex)
                                    : undefined
                                }
                                onMouseMove={
                                  props.rangeSelection
                                    ? () => handleMouseMove(rowIndex, colIndex)
                                    : undefined
                                }
                              >
                                {cell.column.cellRender
                                  ? cell.column.cellRender(row.original)
                                  : cell.render("Cell")}
                                {/* {cell.render('Cell')} */}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                )}
                {shouldShowSummaryRow && (
                  <tfoot className={styles.footer}>
                    <tr>
                      {(headerGroups[headerGroups.length - 1]?.headers ?? []).map(
                        (column: any, idx: number) => {
                          // Chỉ xử lý leaf column
                          const summaryItem = props.summary?.items?.find(
                            (item) => item.column === column.dataField
                          );

                          if (summaryItem && summaryItem.visible === false) {
                            return (
                              <td
                                key={idx}
                                style={{
                                  width: column.width || "40px",
                                  minWidth: column.minWidth,
                                }}
                              />
                            );
                          }

                          let value = summaryItem
                            ? summaryData[summaryItem.column] || 0
                            : "";

                          if (
                            column.summary === true &&
                            column.dataField &&
                            !summaryItem
                          ) {
                            value = summaryData[column.dataField] || 0;
                          }

                          const isFixed = column.fixed;
                          const fixedPosition = column.fixedPosition;
                          const leftPos =
                            isFixed &&
                            (!fixedPosition || fixedPosition === "left")
                              ? leftPositions[
                                  column.id || column.dataField || idx
                                ]
                              : undefined;
                          const rightPos =
                            isFixed && fixedPosition === "right"
                              ? rightPositions[
                                  column.id || column.dataField || idx
                                ]
                              : undefined;

                          return (
                            <td
                              key={idx}
                              style={{
                                width: column.width || "40px",
                                minWidth: column.minWidth,
                                textAlign: column.align || "right",
                                fontWeight: "600",
                                position: isFixed ? "sticky" : "relative",
                                zIndex: isFixed ? 2 : 1,
                                backgroundColor: "#fff",
                                ...(leftPos !== undefined
                                  ? {
                                      left: `${leftPos}px`,
                                    }
                                  : {}),
                                ...(rightPos !== undefined
                                  ? {
                                      right: `${rightPos}px`,
                                    }
                                  : {}),
                              }}
                              className={clsx(
                                isFixed && styles.fixed_column,
                                isFixed && fixedPosition === "right"
                                  ? styles.fixed_column_right
                                  : isFixed
                                  ? styles.fixed_column_left
                                  : undefined
                              )}
                            >
                              {value !== ""
                                ? summaryItem
                                  ? summaryItem.render(Number(value))
                                  : formatVND(Number(value))
                                : ""}
                            </td>
                          );
                        }
                      )}
                    </tr>
                  </tfoot>
                )}
                {pagedData.length <= 0 && (
                  <tbody>
                    <tr>
                      <td colSpan={props.columns.length}>
                        {props.emptyComponent}
                      </td>
                    </tr>
                  </tbody>
                )}
              </>
            )}
          </table>
        </Box>
        {paging?.enable === true && pageCount > 0 && (
          <Box
            id="paging"
            className={clsx(
              styles.pagingContainer,
              hasScroll && styles.hasScrollPagination
            )}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box sx={{ flex: 1 }}>
              {props.paging?.pageSizeItems && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "10px",
                      color: "fg.muted",
                    }}
                  >
                    Page size&nbsp;&nbsp;
                  </Box>
                  {props.paging?.pageSizeItems.map((x) => {
                    return (
                      <Button
                        key={x}
                        className={clsx(
                          styles.pageSize,
                          x === pageSize ? styles.selected : ""
                        )}
                        variant="invisible"
                        sx={{
                          color: "fg.default",
                          fontWeight: 400,
                        }}
                        size="small"
                        onClick={() => {
                          setPageSize(x);
                          setPageIndex(0);
                        }}
                      >
                        {x.toString()}
                      </Button>
                    );
                  })}
                </Box>
              )}
            </Box>
            <Box id="info" sx={{ width: "100px", textAlign: "center" }}>
              <Box
                sx={{
                  fontSize: "12px",
                  color: "fg.muted",
                }}
              >
                {pageIndex * pageSize + 1} -{" "}
                {(pageIndex + 1) * pageSize > data.length
                  ? data.length
                  : (pageIndex + 1) * pageSize}{" "}
                of {data.length}
              </Box>
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "row-reverse" }}
            >
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  currentPage={pageIndex + 1}
                  showPages={{
                    narrow: false,
                  }}
                  onPageChange={(_, n) => {
                    setPageIndex(n - 1);
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DataTable;
